import { useTranslation } from 'react-i18next';
import type { ConversationSummaryDto } from 'korint-sdk';
import { Button } from '../../components/ui/button';
import { ConversationEmptyState } from '../../components/ai-elements/conversation';
import { MessageSquareIcon, TrashIcon } from 'lucide-react';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function ConversationList({
  conversations,
  activeId,
  onSelect,
  onDelete,
  isDeleting,
}: {
  conversations: ConversationSummaryDto[];
  activeId?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}) {
  const { t } = useTranslation();

  if (conversations.length === 0) {
    return (
      <div className="px-2 py-4">
        <ConversationEmptyState
          icon={<MessageSquareIcon className="size-8" />}
          title={t('conversation.noConversations')}
          description={t('conversation.noConversationsDescription')}
        />
      </div>
    );
  }

  return (
    <Stack gap={1} className="px-2">
      {conversations.map(conv => (
        <Group
          justify="between"
          gap={0}
          key={conv.id}
          className={`rounded-md px-2 py-2 cursor-pointer transition-colors ${
            conv.id === activeId ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
          }`}
          onClick={() => onSelect(conv.id)}
        >
          <div className="min-w-0 flex-1">
            <Text size="sm" weight="medium" truncate>
              {conv.title ?? t('conversation.newConversation')}
            </Text>
            <Text size="xs" tone="muted">
              {new Date(conv.updatedAt).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 h-7 w-7 opacity-0 group-hover:opacity-100 hover:opacity-100"
            disabled={isDeleting}
            onClick={e => {
              e.stopPropagation();
              onDelete(conv.id);
            }}
          >
            <TrashIcon className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </Group>
      ))}
    </Stack>
  );
}
